import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import {
  getTransactionalData,
  includeExcludeActionUpdate,
  downloadPrepareDetailsData,
  getFileSummary,
  calculateTDSRate,
  getNegativeEnteires,
  getTotalSum,
  reprocessValidation,
} from '../api/prepare.js'

import { getValidCriteria, DEFAULT_PAGE, sanitizeForLog } from '../constants/index.js'
import { getColumnsForTab, statusTabs, ACTIONS, PREPARE_TABS } from '../constants/prepare.jsx'
import { getEditableSchemaForTab } from '../components/Returns/Prepare/prepareSchema.jsx'
import { z } from 'zod'
import { fetchEntityListIfNeeded } from '../store/actions/entityTanActions.js'
import { ROLE_CAPABILITIES } from '../utils/role-capabilities.js'
import { useFullScreen } from '../contexts/FullScreenContext.jsx'
import { useSidebar } from '../contexts/SidebarContext.jsx'
import { useSnackbar } from '../contexts/SnackbarProvider.jsx'
import { useDispatch, useSelector } from 'react-redux'
import { getGenAiStatus } from '@/api/premiumFeature'

const ddMMyyyyListZod = z
  .string()
  .regex(
    /^((0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4}(\s*,\s*(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/\d{4})*)?$/,
    'Invalid Input. Example: 29/08/2025, 01/09/2025'
  )

// Function to generate search fields dynamically
const getSearchFieldsForTab = (tabStatus, isGenAiEnabled) => {
  const columns = getColumnsForTab(tabStatus, isGenAiEnabled)

  return columns.map(col => {
    if (col.id === 'postingDate' || col.id === 'dateOfDeduction'
      || col.id === 'invoiceDate' || col.id === 'referenceDate'
      || col.id === 'accountingDocumentDate' || col.id === 'dateOfTaxDeposited'
      || col.id === 'dateOfTaxDeposited'
    ) {
      return {
        ...col,
        type: 'text',
        placeholder: `Example: 25/08/2025 (dd/mm/yyyy)`,
        zod: ddMMyyyyListZod,
      }
    }
    return {
      ...col,
      type: col.type || 'text',
      placeholder: `Enter ${col.label}`,
    }
  })
}

export const usePrepare = () => {
  const dispatch = useDispatch()
  const roleTypes = useSelector(state => state.user.role)
  const roleConfig = ROLE_CAPABILITIES[roleTypes] || ROLE_CAPABILITIES.default
  const { isFullScreen, toggleFullScreen, exitFullScreen } = useFullScreen()
  const { setIsCollapsed } = useSidebar()
  const [selectedTab, setSelectedTab] = useState(1)
  const [selectedSubTab, setSelectedSubTab] = useState(-1)
  const [selectedRows, setSelectedRows] = useState([])
  const [editMode, setEditMode] = useState(false)
  const [editingData, setEditingData] = useState({})
  const [transactions, setTransactions] = useState([])
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [defaultSearchCOList, setDefaultSearchCOList] = useState([])
  const [includeSwitchOn, setIncludeSwicthOn] = useState(false)
  const [fileSummaryDetails, setSummaryDetails] = useState([])
  const [totalSumData, setTotalSumData] = useState({})
  const [totalTransactions, setTotalTranscations] = useState(0)

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(0)

  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] = useState(null)

  const [transactionData, setTransactionData] = useState([])
  const [loading, setLoading] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [isSuggestedRateLoading, setIsSuggestedRateLoading] = useState(false)
  const [isReprocessLoading, setIsReprocessLoading] = useState(false)
  const [isCooldown, setIsCooldown] = useState(false)
  const [isGenAiEnabled, setIsGenAiEnabled] = useState(false)
  const [downloadPopperOpen, setDownloadPopperOpen] = useState(false)
  const cooldownTimerRef = useRef(null)



  const { showSnackbar } = useSnackbar()

  useEffect(() => {
    const payload = {
      pageFilter: {
        currentPage: 1,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'entityName',
        sortOrder: 'ASC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
    }
    if (roleConfig.canCreate) {
      dispatch(fetchEntityListIfNeeded(payload))
    }
  }, [dispatch, roleConfig.canCreate])

  const fetchSummary = async defaultSearchCOList => {
    const { tan, periodFilters } = defaultSearchCOList
    try {
      const summaryData = await getFileSummary(tan, periodFilters)
      const summaryList = summaryData?.data?.data || []
      setSummaryDetails(summaryList)
      const totalSum = summaryList
        .filter(item => item.uploadDataCategory !== 'EXCEPTION')
        .reduce((sum, item) => sum + (item.totalRecords || 0), 0)

      setTotalTranscations(totalSum)
    } catch (err) {
      console.error('Error fetching file summary:')
    }
  }


  const fetchTransactionData = async ({
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    screenName = statusTabs[selectedTab].screenName,
    defaultSearch = defaultSearchCOList,
  } = {}) => {
    const { tan, periodFilters } = defaultSearch || {}

    const subTabScreenName =
      selectedTab === 4 &&
      statusTabs[selectedTab].children &&
      statusTabs[selectedTab].children.filter(
        child => child.id === selectedSubTab
      )[0]?.screenName
    setLoading(true)

    try {
      const payload = {
        pageFilter: {
          currentPage: page,
          includeCount: true,
          pageSize: pageSize,
          sortField: 'loadId',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: searchList,
        entityTANs: tan ? tan.split(',') : [],
        periodFilters
      }

      // call three APIs in parallel
      const [transactionRes, sumRes, genAiResponse] = await Promise.all([
        getTransactionalData(payload, screenName),
        getTotalSum(payload, screenName),
        getGenAiStatus()
      ])
      await fetchSummary(defaultSearch)

      // handle transactional data
      const newData = transactionRes?.data?.data?.content || []
      setTransactionData(newData)
      setTotalItems(transactionRes?.data?.data?.totalElements)
      setSelectedRows([])
      // handle sum data
      const totalSum = sumRes?.data?.data || {}
      setTotalSumData(totalSum)
      // handle gen ai data
      const isGenAiEnabled = genAiResponse?.success &&  genAiResponse?.data?.data
      setIsGenAiEnabled(isGenAiEnabled)
    } catch (error) {
      console.error('Error fetching transactional data & total sum:')
    } finally {
      setLoading(false)
    }
  }
  // Memoized computed values
  const currentStatus = useMemo(() => {
    const currentTab = statusTabs[selectedTab]
    if (currentTab.children && selectedTab === 4) {
      const subTabStatus = currentTab.children.filter(
        child => child.id === selectedSubTab
      )[0]?.status
      return subTabStatus
    }
    return currentTab.status
  }, [selectedTab, selectedSubTab])

  const searchFields = useMemo(
    () => getSearchFieldsForTab(currentStatus, isGenAiEnabled),
    [currentStatus, isGenAiEnabled]
  )

  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  const currentTransactions = useMemo(
    () => transactions[currentStatus] || [],
    [transactions, currentStatus]
  )

  const currentColumns = useMemo(
    () => getColumnsForTab(currentStatus, isGenAiEnabled),
    [currentStatus, isGenAiEnabled]
  )

  const [visibleColumns, setVisibleColumns] = useState(
    currentColumns.map(col => col.id)
  )

  useEffect(() => {
    setVisibleColumns(currentColumns.map(col => col.id))
  }, [currentColumns])

  const selectedCount = selectedRows.length

  const emptyMessage = useMemo(() => {
    const tabLabel =
      selectedTab === 4 && statusTabs[selectedTab].children
        ? statusTabs[selectedTab].children
          .filter(child => child.id === selectedSubTab)[0]
          ?.label.toLowerCase()
        : statusTabs[selectedTab].label.toLowerCase()
    return `No ${tabLabel} transactions found`
  }, [selectedTab, selectedSubTab])

  const mapApiResponseToTableData = apiData => {
    return apiData?.map((parent, parentIdx) => {
      const parentUuid =
        parent.vendorTransactionalDataUuid || `parent-${parentIdx}`

      return {
        ...parent,
        uuid: parentUuid,
        referenceNumber: '-',
        isChild: false,
        hasChildren: parent.hasChildren,
        children: (parent.negativeTransactions || []).map((child, idx) => {
          const refNoParts = []
          if (child.provisionAdjustmentRefNo)
            refNoParts.push(child.provisionAdjustmentRefNo)
          if (child.advanceAdjustmentRefNo)
            refNoParts.push(child.advanceAdjustmentRefNo)

          return {
            ...child,
            uuid:
              child.vendorTransactionalDataUuid || `${parentUuid}-child-${idx}`,
            parentId: parentUuid,
            isChild: true,
            referenceNumber:
              refNoParts.length > 0 ? refNoParts.join(' / ') : null,
          }
        }),
      }
    })
  }

  const fetchNegativeData = async (searchList = [], viewData = defaultSearchCOList) => {
    const screenName = statusTabs[selectedTab]?.screenName
    const { tan, periodFilters } = viewData || {}
    const subTabScreenName =
      selectedTab === 4 &&
      statusTabs[selectedTab].children &&
      statusTabs[selectedTab].children.filter(
        child => child.id === selectedSubTab
      )[0]?.screenName

    if (screenName !== 'NEGATIVE') return
    setLoading(true)

    const payload = {
      pageFilter: {
        currentPage: currentPage,
        includeCount: true,
        pageSize: itemsPerPage,
        sortField: 'loadId',
        sortOrder: 'ASC',
      },
      isReportDownload: false,
      searchCOList: getValidCriteria(searchList),
      entityTANs: tan ? tan.split(',') : [],
      periodFilters,
      referenceType: subTabScreenName,
    }

    const [transactionRes, sumRes] = await Promise.all([
      getNegativeEnteires(payload),
      getTotalSum(payload, screenName),
    ])
    await fetchSummary(defaultSearchCOList)
    const tableData = mapApiResponseToTableData(transactionRes?.data?.content)
    const totalSum = sumRes?.data?.data || {}
    setExpandedRows(new Set())
    setTransactionData(tableData)
    setTotalItems(transactionRes?.data?.totalElements)
    setTotalSumData(totalSum)
    setLoading(false)
  }
  useEffect(() => {
    fetchNegativeData()
  }, [selectedSubTab])


  // Event handlers
  const handleTabChange = useCallback(
    async (event, newValue) => {
      const screenName = statusTabs[newValue].screenName
      setSelectedTab(newValue)
      setSelectedRows([])
      setEditMode(false)
      setEditingData({})
      setItemsPerPage(100)
      setCurrentPage(1)

      setSelectedSubTab(-1)
      setSearchCriteria([
        {
          id: 1,
          logicalOperator: 'Where',
          field: searchFields[0]?.id || '',
          value: '',
        },
      ])

      if (screenName === 'NEGATIVE') {
        setSelectedSubTab(1)
        return
      }
      // Only call API if we already have a defaultSearch
      if (defaultSearchCOList && Object.keys(defaultSearchCOList).length > 0) {
        await fetchTransactionData({
          page: 1,
          pageSize: 100,
          defaultSearch: defaultSearchCOList,
          screenName: screenName,
        })
      }
    },
    [defaultSearchCOList, itemsPerPage]
  )

  const handleSubTabChange = useCallback(subTabIndex => {
    setSelectedSubTab(subTabIndex)
    setSelectedRows([])
    setEditMode(false)
    setEditingData({})
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }, [])

  const handleSelectRow = useCallback(id => {
    setSelectedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    )
  }, [])

  const handleSelectAll = useCallback(
    event => {
      setSelectedRows(
        event.target.checked ? transactionData.map(t => t.uuid) : []
      )
    },
    [transactionData]
  )

  const handleEdit = useCallback(() => {
    setEditMode(true)
    const editData = selectedRows.reduce((acc, id) => {
      const transaction = currentTransactions.find(t => t.id === id)
      if (transaction) {
        acc[id] = { ...transaction }
      }
      return acc
    }, {})
    setEditingData(editData)
  }, [selectedRows, currentTransactions])

  const handleFieldChange = (rowId, field, value) => {
    setEditingData(prev => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        [field]: value,
      },
    }))
  }

  function handleSaveEdit() {
    if (!editingData || Object.keys(editingData).length === 0) {
      showSnackbar('No rows to save', 'error')
      return
    }

    const validRowsPayload = []
    const invalidMessages = []

    const editableSchema = getEditableSchemaForTab(currentStatus)

    Object.entries(editingData).forEach(([uuid, changes], idx) => {
      const originalRow = transactionData.find(r => r.uuid === uuid)
      if (!originalRow) {
        invalidMessages.push(`Row for UUID ${uuid} not found`)
        return
      }

      const rowNumber = transactionData.findIndex(r => r.uuid === uuid) + 1

      // Sanitize numeric fields if needed
      const sanitizedChanges = { ...changes }
      if (
        sanitizedChanges.vendorContact !== undefined &&
        sanitizedChanges.vendorContact !== null
      ) {
        sanitizedChanges.vendorContact = String(
          sanitizedChanges.vendorContact
        ).replace(/\D/g, '')
      }

      const mergedRow = { ...originalRow, ...sanitizedChanges }
      const result = editableSchema.safeParse(mergedRow)

      if (result.success) {
        validRowsPayload.push({ uuid, ...sanitizedChanges })
      } else {
        result.error.issues.forEach(issue => {
          const field = issue.path?.[0] ?? 'unknown'
          invalidMessages.push(`Row ${rowNumber} [${field}]: ${issue.message}`)
        })
      }
    })

    if (invalidMessages.length > 0) {
      const delay = 3000
      invalidMessages.forEach((msg, i) =>
        setTimeout(() => showSnackbar(msg, 'error'), i * delay)
      )
      return
    }

    if (validRowsPayload.length === 0) {
      showSnackbar('No valid changes to update', 'error')
      return
    }

    try {
      // Update transactions locally
      setTransactions(prevTransactions => {
        const newTransactions = { ...prevTransactions }

        // Ensure we always have an array for currentStatus
        if (!Array.isArray(newTransactions[currentStatus])) {
          newTransactions[currentStatus] = []
        }

        validRowsPayload?.forEach(({ uuid, ...changes }) => {
          const transactionIndex = newTransactions[currentStatus].findIndex(
            t => t.uuid === uuid
          )

          if (transactionIndex !== -1) {
            // Update existing row
            newTransactions[currentStatus][transactionIndex] = {
              ...newTransactions[currentStatus][transactionIndex],
              ...changes,
            }
          } else {
            // If row doesn't exist, add it
            newTransactions[currentStatus].push({ uuid, ...changes })
          }
        })

        return newTransactions
      })

      setEditMode(false)
      // setEditingData({})
      setSelectedRows([])
      showSnackbar('Changes saved successfully', 'success')
    } catch (err) {
      console.error('Error saving edits:')
      showSnackbar('Failed to save changes', 'error')
    }
  }

  const handleCloseEditMode = () => {
    setEditMode(false)
    setEditingData({})
    setSelectedRows([])
  }

  const handleView = async data => {
    setShowTable(true)
    setDefaultSearchCOList(data)
    if (statusTabs[selectedTab].screenName != "NEGATIVE") {
      await fetchTransactionData({
        page: 1,
        pageSize: itemsPerPage,
        defaultSearch: data,
      })
    } else {
      fetchNegativeData([], data)
    }
  }

  const handleRowExpand = useCallback(rowId => {
    setExpandedRows(prev => {
      const newSet = new Set(prev)
      newSet.has(rowId) ? newSet.delete(rowId) : newSet.add(rowId)
      return newSet
    })
  }, [])


  const handleFullscreen = useCallback(() => {
    toggleFullScreen()
    setIsCollapsed(true)
  }, [])

  const handleFullScreenExit = useCallback(() => {
    exitFullScreen()
  }, [])

  const handleColumnConfigClick = event => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(!columnConfigOpen)
  }

  const handleColumnConfigClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleColumnChange = selectedColumns => {
    setVisibleColumns(selectedColumns)
  }

  const filteredColumns = useMemo(() => {
    return currentColumns.filter(col => visibleColumns.includes(col.id))
  }, [currentColumns, visibleColumns])

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    const screenName = statusTabs[selectedTab]?.screenName
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    if (screenName === 'NEGATIVE') {
      fetchNegativeData(searchList)
    } else {
      await await fetchTransactionData({
        page: 1,
        pageSize: itemsPerPage,
        searchList,
        defaultSearch: defaultSearchCOList,
      })
    }

    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    const screenName = statusTabs[selectedTab]?.screenName

    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    if (screenName === 'NEGATIVE') {
      fetchNegativeData()
    } else {
      await await fetchTransactionData({
        page: 1,
        pageSize: itemsPerPage,
        searchList: [],
        defaultSearch: defaultSearchCOList,
      })
    }
    setSearchOpen(false)
  }

  const handlePageChange = async page => {
    setCurrentPage(page)
    await fetchTransactionData({
      page,
      pageSize: itemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleItemsPerPageChange = async newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    await fetchTransactionData({
      page: 1,
      pageSize: newItemsPerPage,
      searchList: getValidCriteria(searchCriteria),
      defaultSearch: defaultSearchCOList,
    })
  }

  const handleIncludeSwitchChange = event => {
    setIncludeSwicthOn(event.target.checked)
  }

  const handleExcludeItems = async () => {
    const screenName = statusTabs[selectedTab].screenName
    let action = null
    if (screenName === 'PROCESSED') {
      action = ACTIONS.EXCLUDE
    } else if (screenName === 'EXCLUDED') {
      action = ACTIONS.INCLUDE
    }

    if (!action) {
      return
    }

    if (screenName === 'EXCLUDED') {
      const hasOthersCategory = transactionData
        .filter(t => selectedRows.includes(t.uuid))
        .some(t => t.transactionCategory === 'Others')

      if (hasOthersCategory) {
        showSnackbar(
          'Cannot include transactions with Transaction Category "Others"',
          'error'
        )
        return
      }
    }

    try {
      const res = await includeExcludeActionUpdate(selectedRows, action)
      if (res.success) {
        showSnackbar(`${action} successfully`, 'success')
        await fetchTransactionData({
          page: currentPage,
          pageSize: itemsPerPage,
          searchList: getValidCriteria(searchCriteria),
          defaultSearch: defaultSearchCOList,
        })
      } else {
        showSnackbar(res.error, 'error')
      }
    } catch (error) {
      console.error(`${action} API Failed:`)
    }
  }

  const handleSuggestTDSRate = async () => {
    setIsSuggestedRateLoading(true)
    const screenName = statusTabs[selectedTab].screenName
    const { tan, periodFilters } = defaultSearchCOList || {}

    try {
      const res = await calculateTDSRate(tan, periodFilters, screenName)
      if (res?.data?.success) {
        showSnackbar(
          `Rate determination in progress for the selected period`,
          'success'
        )
        await fetchTransactionData({
          page: currentPage,
          pageSize: itemsPerPage,
          searchList: getValidCriteria(searchCriteria),
          defaultSearch: defaultSearchCOList,
        })
      } else {
        showSnackbar(res?.data?.errors[0]?.message, 'error')
      }
    } catch (error) {
      console.error(`API Failed:`, sanitizeForLog(error?.message || 'Unknown error'))
    } finally {
      setIsSuggestedRateLoading(false)
    }
  }

  const handleReprocess = async () => {
    // prevent click during cooldown
    if (isCooldown) return

    setIsReprocessLoading(true)
    setIsCooldown(true)

    // clear old timer if exists
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current)
    }

    // start fresh 30s cooldown
    cooldownTimerRef.current = setTimeout(() => {
      setIsCooldown(false)
    }, 30000)

    const screenName = statusTabs[selectedTab].screenName
    const { tan, periodFilters } = defaultSearchCOList || {}

    const payload = {
      pageFilter: {
        currentPage,
        includeCount: true,
        pageSize: itemsPerPage,
        sortField: 'loadId',
        sortOrder: 'ASC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: getValidCriteria(searchCriteria),
      entityTANs: tan ? tan.split(',') : [],
      periodFilters,
    }

    try {
      const res = await reprocessValidation(payload, screenName)

      if (res?.data?.success) {
        showSnackbar(
          'Reprocessing request has been initiated on selected transactions',
          'success'
        )
      } else {
        showSnackbar(res?.data?.errors?.[0]?.message, 'error')
      }
    } catch (error) {
      console.error('API Failed:', sanitizeForLog(error?.message || 'Unknown error'))
    } finally {
      setIsReprocessLoading(false)
    }
  }


  const handleBulkDownload = async (prepareDownloadType = null) => {
    // Check if there's no data to download
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }
    showSnackbar('Request Initiated', 'info')
    const { tan, periodFilters } = defaultSearchCOList || {}
    const screenName = statusTabs[selectedTab].screenName
    const subTabScreenName =
      selectedTab === 4 &&
      statusTabs[selectedTab].children &&
      statusTabs[selectedTab].children.filter(
        child => child.id === selectedSubTab
      )[0]?.screenName
    const payload = {
      pageFilter: {
        currentPage: currentPage,
        includeCount: true,
        pageSize: DEFAULT_PAGE,
        sortField: 'lastModifiedDate',
        sortOrder: 'DESC',
      },
      requestFilter: [],
      isReportDownload: false,
      searchCOList: [],
      entityTANs: tan ? tan.split(',') : [],
      periodFilters,
      ...(subTabScreenName ? { referenceType: subTabScreenName } : {})
    }
    const result = await downloadPrepareDetailsData(payload, screenName, prepareDownloadType)
    if (result?.success && result?.data)
      showSnackbar(
        `Request Initiated for Download With Id: ${result.data.data}`,
        'success'
      )
    else if (result?.error) showSnackbar(result.error, 'error')
  }

  const handleDownloadIconClick = () => {
    const screenName = statusTabs[selectedTab].screenName
    if (screenName === 'PROCESSED') {
      setDownloadPopperOpen(prev => !prev)
    } else {
      handleBulkDownload()
    }
  }

  const handleDownloadPopperClose = () => {
    setDownloadPopperOpen(false)
  }

  const handleReportDownload = selectedValue => {
    setDownloadPopperOpen(false)
    const prepareDownloadType =
      selectedValue === PREPARE_TABS.PROCESSED ? null : selectedValue
    handleBulkDownload(prepareDownloadType)
  }

  return {
    // State
    selectedTab,
    selectedSubTab,
    selectedRows,
    editMode,
    editingData,
    transactions,
    expandedRows,
    transactionData,
    showTable,
    isFullScreen,
    includeSwitchOn,
    fileSummaryDetails,
    totalSumData,
    totalTransactions,
    // Computed values
    currentStatus,
    currentTransactions,
    currentColumns,
    filteredColumns,
    selectedCount,
    emptyMessage,
    visibleColumns,
    columnConfigOpen,
    columnConfigAnchor,
    searchFields,
    totalItems,
    itemsPerPage,
    currentPage,
    searchCriteria,
    setSearchCriteria,
    searchOpen,
    anchorEl,
    loading,
    isSuggestedRateLoading,
    isCooldown,
    isReprocessLoading,
    downloadPopperOpen,
    // Event handlers
    handleTabChange,
    handleSubTabChange,
    handleSelectRow,
    handleSelectAll,
    handleEdit,
    handleFieldChange,
    handleSaveEdit,
    handleView,
    handleRowExpand,
    handleFullscreen,
    handleFullScreenExit,
    handleColumnConfigClick,
    handleColumnConfigClose,
    handleColumnChange,
    handleCloseSearch,
    handleApplySearch,
    handleResetSearch,
    handleSearch,
    handlePageChange,
    handleItemsPerPageChange,
    handleIncludeSwitchChange,
    handleExcludeItems,
    handleSuggestTDSRate,
    handleCloseEditMode,
    handleReprocess,
    handleDownloadIconClick,
    handleDownloadPopperClose,
    handleReportDownload
  }
}
